import { FC, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { useCurrentUnit } from '@/hooks/use-current-unit';
import { useQrCode } from '@/hooks/use-qr-code';
import { SectionContainerLayout } from '@/layouts/section-container-layout';

export const FriendsFamilyQrView: FC = () => {
  const { data: unit } = useCurrentUnit();
  const unitUuid = unit?.uuid ?? null;
  const {
    dataUrl: unitQrCodeDataUrl,
    error: unitQrCodeError,
    isLoading: isUnitQrCodeLoading,
  } = useQrCode(unitUuid);

  const unitQrFileName = useMemo(() => {
    const baseName =
      unit?.unitName?.trim() || (unit?.id ? `unit-${unit.id}` : 'unit');
    const safeBaseName = baseName.replace(/[^a-zA-Z0-9-_]+/g, '-');
    return `unit-uuid-${safeBaseName}.png`;
  }, [unit?.id, unit?.unitName]);

  return (
    <SectionContainerLayout horizontal className="border-b-0">
      <SectionHeadingGroup
        className="mb-6"
        subtitle="Use this QR Code to register a Friends and Family app for this unit"
        title="Friends and Family App QR Code"
      />
      <div className="w-min min-w-60 grow">
        <Card>
          {unitUuid ? (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex h-40 w-40 items-center justify-center rounded-sm border border-gray-200 bg-white">
                {unitQrCodeDataUrl ? (
                  <img
                    alt="Friends and Family app QR code"
                    className="h-36 w-36"
                    src={unitQrCodeDataUrl}
                  />
                ) : unitQrCodeError ? (
                  <span className="px-3 text-center text-xs text-red-600">
                    Failed to generate QR code.
                  </span>
                ) : isUnitQrCodeLoading ? (
                  <span className="text-xs text-gray-500">
                    Generating QR code...
                  </span>
                ) : (
                  <span className="text-xs text-gray-500">No QR code.</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <div className="text-xs text-gray-500 uppercase">UUID</div>
                <div className="font-mono text-sm break-all text-gray-900">
                  {unitUuid}
                </div>
                <Button
                  {...(unitQrCodeDataUrl
                    ? {
                        as: 'a',
                        download: unitQrFileName,
                        href: unitQrCodeDataUrl,
                      }
                    : {})}
                  disabled={!unitQrCodeDataUrl}
                  variant="secondary"
                >
                  Download QR Code
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">
              No UUID available for this unit.
            </div>
          )}
        </Card>
      </div>
    </SectionContainerLayout>
  );
};
